/**
 * useBranches Hook
 * React hook for managing branches data
 */

import { useState, useEffect, useCallback } from 'react';
import { branchesService, Branch, BranchesQuery } from '../services/branches.service';

interface UseBranchesState {
  branches: Branch[];
  loading: boolean;
  error: string | null;
}

export function useBranches(query?: BranchesQuery) {
  const [state, setState] = useState<UseBranchesState>({
    branches: [],
    loading: false,
    error: null,
  });

  const fetchBranches = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await branchesService.getBranches(query);
      
      // Handle potential double-wrapped response from NestJS TransformInterceptor
      let branchesData: Branch[] = [];
      if (response.data) {
        // Check if data is wrapped in another data property
        branchesData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).data || [];
      }

      setState({ branches: branchesData, loading: false, error: null });
    } catch (err: any) {
      console.error('Failed to fetch branches:', err);
      setState({
        branches: [],
        loading: false,
        error: err.message || 'Failed to load branches',
      });
    }
  }, [query?.tenantId, query?.isActive, query?.search]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const refresh = useCallback(() => {
    fetchBranches();
  }, [fetchBranches]);

  return {
    branches: state.branches,
    loading: state.loading,
    error: state.error,
    refresh,
  };
}
